import React, { useState } from 'react';
import { apiFetch } from '../services/apiClient';
import './ThemeCustomizer.css';

const ThemeCustomizer = ({ jobId, currentSettings, onThemeChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localSettings, setLocalSettings] = useState(currentSettings || {
        form_background_color: '#f5f5f5',
        form_text_color: '#333333',
        form_primary_color: '#667eea'
    });

    const backgroundColorOptions = [
        { value: '#f5f5f5', label: 'Light Gray (Default)' },
        { value: '#FFFFFF', label: 'White' },
        { value: '#e3f2fd', label: 'Light Blue' },
        { value: '#f3e5f5', label: 'Light Purple' },
        { value: '#fff3e0', label: 'Light Orange' },
        { value: '#e8f5e9', label: 'Light Green' },
        { value: '#fce4ec', label: 'Light Pink' },
        { value: '#f1f8e9', label: 'Light Yellow-Green' }
    ];

    const textColorOptions = [
        { value: '#333333', label: 'Dark Gray (Default)' },
        { value: '#000000', label: 'Black' },
        { value: '#424242', label: 'Dark Gray' },
        { value: '#616161', label: 'Medium Gray' },
        { value: '#1976d2', label: 'Blue' },
        { value: '#388e3c', label: 'Green' },
        { value: '#d32f2f', label: 'Red' },
        { value: '#f57c00', label: 'Orange' }
    ];

    const primaryColorOptions = [
        { value: '#667eea', label: 'Purple (Default)' },
        { value: '#1976d2', label: 'Blue' },
        { value: '#388e3c', label: 'Green' },
        { value: '#d32f2f', label: 'Red' },
        { value: '#f57c00', label: 'Orange' },
        { value: '#7b1fa2', label: 'Deep Purple' },
        { value: '#0288d1', label: 'Light Blue' },
        { value: '#00796b', label: 'Teal' }
    ];

    const handleChange = (field, value) => {
        const newSettings = {
            ...localSettings,
            [field]: value
        };
        setLocalSettings(newSettings);
        if (onThemeChange) {
            onThemeChange(newSettings);
        }
    };

    const handleSave = async () => {
        if (jobId) {
            try {
                const response = await apiFetch(`/api/jobs/${jobId}/form-settings`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(localSettings)
                });
                if (response.ok) {
                    setIsOpen(false);
                    if (onThemeChange) {
                        onThemeChange(localSettings);
                    }
                }
            } catch (error) {
                console.error('Error saving theme:', error);
                alert('Failed to save theme. Please try again.');
            }
        } else {
            // Save to localStorage for preview
            localStorage.setItem('applicationFormTheme', JSON.stringify(localSettings));
            setIsOpen(false);
            if (onThemeChange) {
                onThemeChange(localSettings);
            }
        }
    };

    return (
        <>
            <button 
                className="theme-toggle-button"
                onClick={() => setIsOpen(!isOpen)}
                title="Change Theme"
            >
                🎨
            </button>
            
            {isOpen && (
                <div className="theme-customizer-overlay" onClick={() => setIsOpen(false)}>
                    <div className="theme-customizer-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="theme-customizer-header">
                            <h3>Customize Theme</h3>
                            <button className="close-button" onClick={() => setIsOpen(false)}>×</button>
                        </div>
                        
                        <div className="theme-customizer-content">
                            <div className="color-group">
                                <label>Background Color</label>
                                <select
                                    value={localSettings.form_background_color}
                                    onChange={(e) => handleChange('form_background_color', e.target.value)}
                                >
                                    {backgroundColorOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="color-group">
                                <label>Text Color</label>
                                <select
                                    value={localSettings.form_text_color}
                                    onChange={(e) => handleChange('form_text_color', e.target.value)}
                                >
                                    {textColorOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="color-group">
                                <label>Primary Color (Buttons)</label>
                                <select
                                    value={localSettings.form_primary_color}
                                    onChange={(e) => handleChange('form_primary_color', e.target.value)}
                                >
                                    {primaryColorOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="theme-customizer-actions">
                            <button onClick={() => setIsOpen(false)} className="cancel-btn">Cancel</button>
                            <button onClick={handleSave} className="save-btn">Save Theme</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ThemeCustomizer;
